import React, { useState } from 'react';
import {
  ListBase, ListView, TextField, FunctionField, TextInput, SelectInput, useTranslate, useRefresh,
} from 'react-admin';
import { Chip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { ACCOUNT_STATUSES, LOGGING_STATUSES } from '@/config/constants';
import {
  FilterToggle, ListScreen, PageTitle, ResponsiveDatagrid, SectionHeader, StatusDot,
  useActiveFilterCount, useListExport,
} from '@/components/shell';
import { useServerChoices } from '@/features/servers/useServerChoices';
import { AccountActions } from './AccountActions';
import { BulkServerReassignButton, BulkServerReassignDialog } from './BulkServerReassign';
import { loggingLevelOf, type AccountRecord, type AccountServer } from './types';

/** Active in words; a limited or blocked account is the exception, so only those get a chip. */
const AccountStatus: React.FC<{ record: AccountRecord }> = ({ record }) => {
  const t = useTranslate();
  const status = record.accountResponse?.accountStatus;
  if (!status) return null;
  const label = t(`screens.accounts.status.${status}`, { _: status });
  if (status === 'Limited') return <Chip label={label} color="warning" size="small" />;
  if (status === 'Blocked') return <Chip label={label} color="error" size="small" />;
  return <>{label}</>;
};

/** The validity date; an expired one is the exception and gets the chip. */
const Validity: React.FC<{ record: AccountRecord }> = ({ record }) => {
  if (!record.validityTimeFrame) return <>—</>;
  const date = dayjs(record.validityTimeFrame);
  const label = date.format('DD/MM/YYYY');
  return date.isBefore(dayjs()) ? <Chip label={label} color="error" size="small" /> : <>{label}</>;
};

const RateLimit: React.FC<{ record: AccountRecord }> = ({ record }) => {
  const t = useTranslate();
  const acc = record.accountResponse;
  if (!acc?.rateLimitApplies) {
    return <Typography variant="body2" color="text.secondary" component="span">{t('screens.accounts.rateOff')}</Typography>;
  }
  return <>{t('screens.accounts.rate', { min: acc.rateLimitPerMin, day: acc.rateLimitPerDay })}</>;
};

const Server: React.FC<{ server?: AccountServer }> = ({ server }) => {
  if (!server?.name) return <Typography variant="body2" color="text.secondary" component="span">—</Typography>;
  return <StatusDot tone={server.status === 'Online' ? 'ok' : 'problem'} label={server.name} />;
};

const Logging: React.FC<{ record: AccountRecord }> = ({ record }) => {
  const t = useTranslate();
  const level = loggingLevelOf(record.accountResponse?.loggingStatus);
  if (!level) return null;
  return <>{t(`screens.accounts.logging.${level}`, { _: level })}</>;
};

/** Rendered inside the list's context: export, the filter badge and the bulk reassign read it. */
const Header: React.FC<{ filtersOpen: boolean; onToggleFilters: () => void }> = ({ filtersOpen, onToggleFilters }) => {
  const t = useTranslate();
  const navigate = useNavigate();
  const refresh = useRefresh();
  const exportList = useListExport();
  const activeCount = useActiveFilterCount();
  const [bulkOpen, setBulkOpen] = useState(false);

  return (
    <>
      <SectionHeader
        left={
          <PageTitle title={t('resources.accounts.name', { smart_count: 2 })} subtitle={t('screens.accounts.subtitle')} />
        }
        filters={<FilterToggle open={filtersOpen} onToggle={onToggleFilters} activeCount={activeCount} />}
        onRefresh={refresh}
        export={{ label: t('ra.action.export'), ...exportList }}
        actions={[{ label: t('accounts.bulk_server.button'), onClick: () => setBulkOpen(true) }]}
        create={{ label: t('common.create'), onClick: () => navigate('/accounts/create') }}
      />
      <BulkServerReassignDialog open={bulkOpen} onClose={() => setBulkOpen(false)} />
    </>
  );
};

export const AccountList = () => {
  const t = useTranslate();
  const navigate = useNavigate();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { choices: serverChoices } = useServerChoices();

  const statusChoices = ACCOUNT_STATUSES.map((c) => ({ id: c.id, name: t(`screens.accounts.status.${c.id}`) }));
  const loggingChoices = LOGGING_STATUSES.map((c) => ({ id: c.id, name: t(`screens.accounts.logging.${c.id}`) }));

  const filters = [
    <TextInput key="q" source="q" label={t('ra.action.search')} alwaysOn resettable />,
    ...(filtersOpen
      ? [
          <SelectInput key="status" source="AccountStatus" label={t('resources.accounts.fields.accountResponse.accountStatus')} choices={statusChoices} translateChoice={false} alwaysOn emptyText={t('common.filters.all')} />,
          <SelectInput key="log" source="LoggingStatus" label={t('screens.accounts.loggingLabel')} choices={loggingChoices} translateChoice={false} alwaysOn emptyText={t('common.filters.all')} />,
          <SelectInput key="primaryServer" source="PrimaryServerId" label={t('accounts.filter_primary_server')} choices={serverChoices} alwaysOn emptyText={t('common.filters.all')} />,
          <SelectInput key="secondaryServer" source="SecondaryServerId" label={t('accounts.filter_secondary_server')} choices={serverChoices} alwaysOn emptyText={t('common.filters.all')} />,
        ]
      : []),
  ];

  return (
    <ListBase perPage={25} sort={{ field: 'accountName', order: 'ASC' }}>
      <ListScreen header={<Header filtersOpen={filtersOpen} onToggleFilters={() => setFiltersOpen((o) => !o)} />}>
        <ListView filters={filters} actions={false} empty={false}>
          <ResponsiveDatagrid<AccountRecord>
            rowClick="edit"
            bulkActionButtons={<BulkServerReassignButton />}
            mobile={{
              primary: (r) => r.accountResponse?.accountName,
              status: (r) => <AccountStatus record={r} />,
              lines: [
                { label: t('resources.accounts.fields.licenseResponse.licenseName'), render: (r) => r.licenseResponse?.licenseName },
                { label: t('resources.accounts.fields.validityTimeFrame'), render: (r) => <Validity record={r} /> },
                { label: t('resources.accounts.fields.primaryServer'), render: (r) => <Server server={r.accountResponse?.primaryServer} /> },
              ],
              actions: (r) => <AccountActions record={r} />,
              onClick: (r) => navigate(`/accounts/${r.id}`),
            }}
          >
            <FunctionField
              sortBy="accountResponse.accountName"
              label={t('resources.accounts.fields.accountResponse.accountName')}
              render={(r: AccountRecord) => r.accountResponse?.accountName}
            />
            <FunctionField
              sortBy="accountResponse.accountStatus"
              label={t('resources.accounts.fields.accountResponse.accountStatus')}
              render={(r: AccountRecord) => <AccountStatus record={r} />}
            />
            <TextField
              source="licenseResponse.licenseName"
              label={t('resources.accounts.fields.licenseResponse.licenseName')}
              sortBy="licenseResponse.licenseName"
            />
            <FunctionField
              sortBy="validityTimeFrame"
              label={t('resources.accounts.fields.validityTimeFrame')}
              render={(r: AccountRecord) => <Validity record={r} />}
            />
            <FunctionField
              sortBy="accountResponse.rateLimitApplies"
              label={t('resources.accounts.fields.accountResponse.rateLimitApplies')}
              render={(r: AccountRecord) => <RateLimit record={r} />}
            />
            <FunctionField
              sortBy="accountResponse.primaryServer.name"
              label={t('resources.accounts.fields.primaryServer')}
              render={(r: AccountRecord) => <Server server={r.accountResponse?.primaryServer} />}
            />
            <FunctionField
              sortable={false}
              label={t('resources.accounts.fields.secondaryServer')}
              render={(r: AccountRecord) => <Server server={r.accountResponse?.secondaryServer} />}
            />
            <FunctionField
              sortBy="accountResponse.loggingStatus"
              label={t('screens.accounts.loggingLabel')}
              render={(r: AccountRecord) => <Logging record={r} />}
            />
            <FunctionField
              label=""
              textAlign="right"
              sortable={false}
              render={(r: AccountRecord) => <AccountActions record={r} />}
            />
          </ResponsiveDatagrid>
        </ListView>
      </ListScreen>
    </ListBase>
  );
};
